import React, { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functionsEurope } from '../firebase';
import { 
  TrashIcon, 
  ChartBarIcon, 
  CloudIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

const StorageCleanupWidget = () => {
  const [stats, setStats] = useState(null);
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const [isRunningCleanup, setIsRunningCleanup] = useState(false);
  const [cleanupResults, setCleanupResults] = useState(null);
  const [error, setError] = useState(null);
  const [lastCleanup, setLastCleanup] = useState(null);

  // Get cleanup statistics
  const getStats = async () => {
    setIsLoadingStats(true);
    setError(null);
    
    try {
      const getCleanupStats = httpsCallable(functionsEurope, 'getCleanupStats');
      const result = await getCleanupStats();
      setStats(result.data);
    } catch (error) {
      console.error('Error getting cleanup stats:', error);
      setError('Failed to load cleanup statistics: ' + error.message);
    } finally {
      setIsLoadingStats(false);
    }
  };

  // Run manual cleanup
  const runCleanup = async () => {
    if (!window.confirm('Are you sure you want to run storage cleanup? This will permanently delete unused files.')) {
      return;
    }

    setIsRunningCleanup(true);
    setError(null);
    setCleanupResults(null);
    
    try {
      const manualStorageCleanup = httpsCallable(functionsEurope, 'manualStorageCleanup');
      const result = await manualStorageCleanup();
      setCleanupResults(result.data);
      setLastCleanup(new Date().toLocaleString());
      
      // Refresh stats after cleanup
      setTimeout(() => {
        getStats();
      }, 2000);
    } catch (error) {
      console.error('Error running cleanup:', error);
      setError('Failed to run cleanup: ' + error.message);
    } finally {
      setIsRunningCleanup(false);
    }
  };

  // Load stats on component mount
  useEffect(() => {
    getStats();
  }, []);

  return (
    <div className="studio-card rounded-xl p-6" style={{
      background: 'rgba(52, 73, 94, 0.8)',
      border: 'none',
      backdropFilter: 'blur(10px)',
      fontFamily: '"Share Tech Mono", monospace'
    }}>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-black tracking-wider flex items-center gap-2" style={{
          color: '#F1C40F',
          textShadow: '1px 1px 0px #E67E22'
        }}>
          <CloudIcon className="h-6 w-6" style={{ color: '#F1C40F' }} />
          🧹 STORAGE CLEANUP
        </h2>
        <button
          onClick={getStats}
          disabled={isLoadingStats}
          className="retro-button px-3 py-1 text-sm font-bold transition-all duration-100 disabled:opacity-50"
          style={{
            background: 'rgba(52, 73, 94, 0.6)',
            border: '1px solid rgba(241, 196, 15, 0.4)',
            color: '#ECF0F1',
            borderRadius: '6px'
          }}
        >
          {isLoadingStats ? 'Loading...' : '🔄 Refresh'}
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-md flex items-start gap-2" style={{
          background: 'rgba(231, 76, 60, 0.2)',
          border: '1px solid rgba(231, 76, 60, 0.4)'
        }}>
          <ExclamationTriangleIcon className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: '#E74C3C' }} />
          <p className="text-sm font-bold" style={{ color: '#E74C3C' }}>{error}</p>
        </div>
      )}

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="p-3 rounded-lg" style={{
            background: 'rgba(52, 152, 219, 0.2)',
            border: '1px solid rgba(52, 152, 219, 0.4)'
          }}>
            <div className="text-2xl font-black" style={{ color: '#3498DB' }}>{stats.totalFiles}</div>
            <div className="text-sm font-bold" style={{ color: '#ECF0F1' }}>Total Files</div>
          </div>
          
          <div className="p-3 rounded-lg" style={{
            background: 'rgba(46, 204, 113, 0.2)',
            border: '1px solid rgba(46, 204, 113, 0.4)'
          }}>
            <div className="text-2xl font-black" style={{ color: '#2ECC71' }}>{stats.referencedFiles}</div>
            <div className="text-sm font-bold" style={{ color: '#ECF0F1' }}>In Use</div>
          </div>
          
          <div className="p-3 rounded-lg" style={{
            background: 'rgba(230, 126, 34, 0.2)',
            border: '1px solid rgba(230, 126, 34, 0.4)'
          }}>
            <div className="text-2xl font-black" style={{ color: '#E67E22' }}>{stats.unusedFiles}</div>
            <div className="text-sm font-bold" style={{ color: '#ECF0F1' }}>Unused</div>
          </div>
          
          <div className="p-3 rounded-lg" style={{
            background: 'rgba(155, 89, 182, 0.2)',
            border: '1px solid rgba(155, 89, 182, 0.4)'
          }}>
            <div className="text-2xl font-black" style={{ color: '#9B59B6' }}>{stats.estimatedStorageSavings}</div>
            <div className="text-sm font-bold" style={{ color: '#ECF0F1' }}>Potential Savings</div>
          </div>
        </div>
      )}

      {cleanupResults && (
        <div className="mb-6 p-4 rounded-md" style={{
          background: 'rgba(46, 204, 113, 0.2)',
          border: '1px solid rgba(46, 204, 113, 0.4)'
        }}>
          <div className="flex items-center gap-2 mb-2">
            <CheckCircleIcon className="h-5 w-5" style={{ color: '#2ECC71' }} />
            <h3 className="font-black" style={{ color: '#2ECC71' }}>✅ CLEANUP COMPLETED!</h3>
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="font-bold" style={{ color: '#ECF0F1' }}>Files Scanned:</span> 
              <span className="font-black ml-1" style={{ color: '#F1C40F' }}>{cleanupResults.totalFilesScanned}</span>
            </div>
            <div>
              <span className="font-bold" style={{ color: '#ECF0F1' }}>Files Deleted:</span> 
              <span className="font-black ml-1" style={{ color: '#F1C40F' }}>{cleanupResults.filesDeleted}</span>
            </div>
            <div>
              <span className="font-bold" style={{ color: '#ECF0F1' }}>Errors:</span> 
              <span className="font-black ml-1" style={{ color: cleanupResults.errors > 0 ? '#E74C3C' : '#F1C40F' }}>{cleanupResults.errors}</span>
            </div>
            <div>
              <span className="font-bold" style={{ color: '#ECF0F1' }}>Duration:</span> 
              <span className="font-black ml-1" style={{ color: '#F1C40F' }}>{Math.round(cleanupResults.durationMs / 1000)}s</span>
            </div>
          </div>
          {lastCleanup && (
            <p className="text-xs font-bold mt-2" style={{ color: '#2ECC71' }}>Last run: {lastCleanup}</p>
          )}
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3">
        <button
          onClick={runCleanup}
          disabled={isRunningCleanup || !stats}
          className="retro-button flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md font-bold transition-all duration-100 disabled:opacity-50"
          style={{
            background: isRunningCleanup ? 'rgba(149, 165, 166, 0.6)' : 'linear-gradient(135deg, #E74C3C, #C0392B)',
            border: '2px solid #F1C40F',
            color: '#ECF0F1',
            textShadow: '1px 1px 0px #A93226'
          }}
        >
          {isRunningCleanup ? (
            <>
              <ClockIcon className="h-4 w-4 animate-spin" />
              🔄 Running Cleanup...
            </>
          ) : (
            <>
              <TrashIcon className="h-4 w-4" />
              🗑️ Run Manual Cleanup
            </>
          )}
        </button>
        
        <button
          onClick={getStats}
          disabled={isLoadingStats}
          className="retro-button flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md font-bold transition-all duration-100 disabled:opacity-50"
          style={{
            background: 'rgba(52, 73, 94, 0.6)',
            border: '1px solid rgba(241, 196, 15, 0.4)',
            color: '#ECF0F1'
          }}
        >
          <ChartBarIcon className="h-4 w-4" />
          {isLoadingStats ? '⏳ Loading...' : '📊 Get Statistics'}
        </button>
      </div>

      <div className="mt-4 p-3 rounded-md" style={{
        background: 'rgba(52, 73, 94, 0.6)',
        border: '1px solid rgba(241, 196, 15, 0.3)'
      }}>
        <h4 className="font-black mb-2 uppercase tracking-wider" style={{ color: '#F1C40F' }}>
          🔧 HOW IT WORKS:
        </h4>
        <ul className="text-sm font-bold space-y-1" style={{ color: '#ECF0F1' }}>
          <li>• Scans all uploaded files in Firebase Storage</li>
          <li>• Checks which files are referenced in your database</li>
          <li>• Identifies unused files older than 7 days</li>
          <li>• Safely deletes orphaned files to reduce storage costs</li>
          <li>• Automatic cleanup runs every Sunday at 2 AM UTC</li>
        </ul>
      </div>
    </div>
  );
};

export default StorageCleanupWidget; 